import { useTranslation } from "react-i18next";

import { countsAsAvailable } from "./api";
import type { ChargerInfo } from "./api";

type Props = {
  chargers: ReadonlyArray<ChargerInfo>;
  testIdSuffix?: string;
};

export type AvailabilityTone = "available" | "busy" | "offline" | "unknown";

const TONE_CLASS: Record<AvailabilityTone, { ring: string; bg: string; dot: string; label: string }> = {
  available: {
    ring: "ring-1 ring-volt/30",
    bg: "bg-volt/10",
    dot: "bg-volt shadow-[0_0_12px_-2px_var(--color-volt-glow)] animate-[live-pulse_2s_ease-out_infinite]",
    label: "text-volt-deep",
  },
  busy: {
    ring: "ring-1 ring-amber/30",
    bg: "bg-amber/10",
    dot: "bg-amber",
    label: "text-amber",
  },
  offline: {
    ring: "ring-1 ring-ember/30",
    bg: "bg-ember/10",
    dot: "bg-transparent border border-ember",
    label: "text-ember",
  },
  unknown: {
    ring: "ring-1 ring-fg-faint/25",
    bg: "bg-fg-faint/10",
    dot: "bg-transparent border border-dashed border-fg-faint",
    label: "text-fg-mute",
  },
};

function summarize(chargers: ReadonlyArray<ChargerInfo>): {
  available: number;
  total: number;
  tone: AvailabilityTone;
  i18nKey: string;
} {
  const total = chargers.length;
  if (total === 0) {
    return { available: 0, total: 0, tone: "unknown", i18nKey: "stations.available_pill_unknown" };
  }
  // All-unreported reads N/N (not 0/N); the freshness badge carries the
  // "no recent reports" caveat. See countsAsAvailable.
  const available = chargers.filter((c) => countsAsAvailable(c.status)).length;
  if (available === total) {
    return { available, total, tone: "available", i18nKey: "stations.available_pill_all" };
  }
  if (available > 0) {
    return { available, total, tone: "available", i18nKey: "stations.available_pill_some" };
  }
  const allOffline = chargers.every((c) => c.status === "offline");
  if (allOffline) {
    return { available: 0, total, tone: "offline", i18nKey: "stations.available_pill_none_offline" };
  }
  return { available: 0, total, tone: "busy", i18nKey: "stations.available_pill_none_busy" };
}

export function AvailabilityPill({ chargers, testIdSuffix = "" }: Props) {
  const { t } = useTranslation();
  const { available, total, tone, i18nKey } = summarize(chargers);
  if (total === 0) return null;
  const cls = TONE_CLASS[tone];
  const label = t(i18nKey, { available, total });
  const aria = t("stations.available_pill_aria", { label });
  const suffix = testIdSuffix ? `-${testIdSuffix}` : "";
  return (
    <span
      data-testid={`availability-pill${suffix}`}
      data-tone={tone}
      role="status"
      aria-label={aria}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${cls.bg} ${cls.ring} ${cls.label}`}
    >
      <span aria-hidden="true" className={`inline-block h-2 w-2 rounded-full ${cls.dot}`} />
      <span className="num tracking-[0.04em]">{label}</span>
    </span>
  );
}
